import { SearchSelect } from '@/components/ui/search-select';
import { School } from '../../../../types/School';
import { useEffect, useMemo, useState } from 'react';
import { addSchool, fetchSchools } from '../../../../services/schools';
import { getUserSchool, updateUserSchool } from '../../../../services/users';
import { useSelector } from 'react-redux';
import { RootState, store } from '../../../../store/store';
import { hapticsImpactLight } from '../../../../utils/haptics';
import { isUserInUS } from '../../../../utils/isUserInUS';

import _ from 'lodash';
import { examCreated } from '../../../../slices/exams/examsSlice';
import { schoolReceived } from '../../../../slices/school/schoolSlice';
import { fetchBulkLecturePreviews } from '../../../../slices/lectures/lectureLoadThunks';
import AddSchoolModal from './AddSchoolModal';

// Custom filter function that matches against both school names and domains (acronyms)
const isSchool = (option: SchoolOption): option is School => {
  return option.id !== 'add-school';
};

const EARTH_RADIUS_MILES = 3958.8;
const MAX_NEARBY_DISTANCE_MILES = 10;
const toRadians = (degrees: number) => (degrees * Math.PI) / 180;

const calculateDistanceMiles = (
  coordA: { lat: number; long: number },
  coordB: { lat: number; long: number }
) => {
  const latDiff = toRadians(coordB.lat - coordA.lat);
  const lonDiff = toRadians(coordB.long - coordA.long);

  const a =
    Math.sin(latDiff / 2) * Math.sin(latDiff / 2) +
    Math.cos(toRadians(coordA.lat)) *
      Math.cos(toRadians(coordB.lat)) *
      Math.sin(lonDiff / 2) *
      Math.sin(lonDiff / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return EARTH_RADIUS_MILES * c;
};

const getNearestDistanceToSchool = (
  school: School,
  location: { lat: number; long: number }
) => {
  if (!school.latLong || school.latLong.length === 0) {
    return null;
  }

  return school.latLong.reduce<number | null>((closest, entry) => {
    const lat = Number(entry.lat);
    const long = Number(entry.long);
    if (Number.isNaN(lat) || Number.isNaN(long)) {
      return closest;
    }

    const distance = calculateDistanceMiles(location, { lat, long });
    if (Number.isNaN(distance)) {
      return closest;
    }

    if (closest === null || distance < closest) {
      return distance;
    }
    return closest;
  }, null);
};

type SchoolOption =
  School | { id: 'add-school'; name: string; verified: boolean };

const ADD_SCHOOL_OPTION: SchoolOption = {
  id: 'add-school',
  name: 'Add School',
  verified: true,
};

interface SchoolSelectorProps {
  onSelect?: (school: School) => void;
  preFetchLectures?: boolean;
  enableLocationLookup?: boolean;
  preselectUserSchool?: boolean;
  updateUserSelection?: boolean;
  showAddSchoolOption?: boolean;
  context?: 'default' | 'modal';
}

const SchoolSelector: React.FC<SchoolSelectorProps> = ({
  onSelect,
  preFetchLectures = false,
  enableLocationLookup = true,
  preselectUserSchool = true,
  updateUserSelection = true,
  showAddSchoolOption = true,
  context = 'default',
}) => {
  const currentUser = useSelector((state: RootState) => state.user.profile);
  const userSchool = useSelector((state: RootState) => state.school.school);
  const [allSchools, setAllSchools] = useState<School[]>([]);
  const [isAddSchoolModalOpen, setIsAddSchoolModalOpen] = useState(false);
  const [newSchoolName, setNewSchoolName] = useState('');

  const [userLocation, setUserLocation] = useState<{
    lat: number;
    long: number;
  } | null>(null);

  const [nearbySchoolIds, setNearbySchoolIds] = useState<string[]>([]);
  const [hasRequestedLocation, setHasRequestedLocation] = useState(false);

  const userIsInUS = useMemo(
    () => isUserInUS(currentUser?.timezone),
    [currentUser?.timezone]
  );

  const medicalSchools = useMemo(() => {
    if (!userIsInUS) {
      return allSchools;
    }
    return allSchools.filter((school) => school.country === 'US');
  }, [allSchools, userIsInUS]);

  const selectedSchoolValue = (() => {
    if (!preselectUserSchool) return null;
    if (!userSchool || medicalSchools.length === 0) return null;

    // First try to find in the local medicalSchools array
    const foundSchool = medicalSchools.find(
      (school) => school.id === userSchool.id
    );

    if (foundSchool) return foundSchool;

    if (!userIsInUS || userSchool.country === 'US') {
      return userSchool;
    }
    return null;
  })();

  const orderedSchools = useMemo(() => {
    if (!enableLocationLookup || nearbySchoolIds.length === 0) {
      return medicalSchools;
    }

    const nearbySet = new Set(nearbySchoolIds);

    const nearbyOrdered = nearbySchoolIds
      .map((id) => medicalSchools.find((school) => school.id === id))
      .filter(Boolean) as School[];

    const remaining = medicalSchools.filter(
      (school) => !nearbySet.has(school.id)
    );

    return [...nearbyOrdered, ...remaining];
  }, [enableLocationLookup, medicalSchools, nearbySchoolIds]);

  const autocompleteOptions: SchoolOption[] = useMemo(
    () =>
      showAddSchoolOption
        ? [...orderedSchools, ADD_SCHOOL_OPTION]
        : orderedSchools,
    [orderedSchools, showAddSchoolOption]
  );

  const schoolSelectOptions = useMemo(
    () =>
      autocompleteOptions.map((option) => {
        if (!isSchool(option)) {
          return {
            label: option.name,
            value: option.id,
            searchText: option.name,
            alwaysVisible: true,
          };
        }

        const domains = (option.domains || []).join(' ');
        const keywords = (option.keywords || []).join(' ');
        const shortName = option.shortName || '';

        return {
          label: option.name,
          value: option.id,
          searchText: `${option.name} ${domains} ${keywords} ${shortName}`,
        };
      }),
    [autocompleteOptions]
  );

  useEffect(() => {
    getSchools();
  }, []);

  const requestUserLocation = () => {
    if (!enableLocationLookup) {
      return;
    }
    if (hasRequestedLocation) {
      return;
    }
    setHasRequestedLocation(true);

    if (typeof window === 'undefined' || !('geolocation' in navigator)) {
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserLocation({
          lat: position.coords.latitude,
          long: position.coords.longitude,
        });
      },
      () => {
        // noop
      },
      {
        maximumAge: 5 * 60 * 1000,
      }
    );
  };

  useEffect(() => {
    if (
      preFetchLectures &&
      selectedSchoolValue?.id &&
      selectedSchoolValue.exams &&
      selectedSchoolValue.exams.length > 0
    ) {
      const firstExamId = selectedSchoolValue.exams[0].id;
      if (firstExamId) {
        void store.dispatch(fetchBulkLecturePreviews({ examId: firstExamId }));
      }
    }
  }, [selectedSchoolValue?.id]);

  useEffect(() => {
    if (!enableLocationLookup || !userLocation || medicalSchools.length === 0) {
      setNearbySchoolIds([]);
      return;
    }

    const nearby = medicalSchools
      .map((school) => ({
        schoolId: school.id,
        distance: getNearestDistanceToSchool(school, userLocation),
      }))
      .filter(
        (item) =>
          item.distance !== null &&
          typeof item.distance === 'number' &&
          item.distance <= MAX_NEARBY_DISTANCE_MILES
      )
      .sort((a, b) => (a.distance || 0) - (b.distance || 0))
      .map((item) => item.schoolId);

    setNearbySchoolIds(nearby);
  }, [enableLocationLookup, userLocation, medicalSchools]);

  const getSchools = async () => {
    try {
      const schools = await fetchSchools();
      if (schools) {
        const visibleSchools = schools.filter(
          (school) => school.status !== 'DELETE'
        );

        const verifiedSchools = visibleSchools.filter(
          (school) =>
            school.verified ||
            (!school.verified &&
              !_.isNil(currentUser?.schoolId) &&
              currentUser?.schoolId === school.id) ||
            currentUser?.id === school.creatorId
        );

        const sortedSchools = [...verifiedSchools].sort((a, b) =>
          a.name.localeCompare(b.name)
        );

        setAllSchools(sortedSchools);
      }
      if (currentUser?.id) {
        getUserSchool(currentUser.id);
      }
    } catch (error) {
      console.error('Error fetching schools:', error);
    }
  };

  const handleAddSchool = async () => {
    if (!currentUser?.id) {
      return;
    }
    try {
      const newSchool = await addSchool(newSchoolName, currentUser?.id);
      if (newSchool) {
        setAllSchools((prev) => [...prev, newSchool]);
        optimisticallyUpdateSchoolRedux(newSchool);
      }
      getSchools();
    } catch (error) {
      console.error('Error adding school:', error);
    }
  };

  const optimisticallyUpdateSchoolRedux = (school: School | string) => {
    if (currentUser?.id) {
      // If a school object is passed directly, use it; otherwise find by school ID
      const selectedSchool =
        typeof school === 'string'
          ? allSchools.find((s) => s.id === school)
          : school;

      if (selectedSchool) {
        store.dispatch(schoolReceived(selectedSchool));
        const selectedExams = selectedSchool.exams;
        if (selectedExams && selectedExams.length > 0) {
          for (const exam of selectedExams) {
            store.dispatch(examCreated(exam));
          }
        }
      }
    }
  };

  const handleOptionSelect = (option: SchoolOption) => {
    if (option.id === 'add-school') {
      if (!showAddSchoolOption) {
        return;
      }
      hapticsImpactLight();
      setIsAddSchoolModalOpen(true);
      return;
    }

    if (!isSchool(option)) return;

    if (updateUserSelection) {
      if (!currentUser?.id) return;
      optimisticallyUpdateSchoolRedux(option.id);
      updateUserSchool(currentUser.id, option.id);
    }
    onSelect?.(option);
  };

  return (
    <div className="school-selector">
      <SearchSelect
        options={schoolSelectOptions}
        value={selectedSchoolValue?.id}
        placeholder="Select school"
        searchPlaceholder="Search schools..."
        emptyText="No schools found."
        disabled={!!selectedSchoolValue && currentUser?.ambassador}
        context={context}
        onOpenChange={(open) => {
          if (open && enableLocationLookup) {
            requestUserLocation();
          }
        }}
        onValueChange={(nextId) => {
          if (!nextId) {
            return;
          }

          const selectedOption = autocompleteOptions.find(
            (option) => option.id === nextId
          );

          if (!selectedOption) return;
          handleOptionSelect(selectedOption);
        }}
      />

      <AddSchoolModal
        open={isAddSchoolModalOpen}
        newSchoolName={newSchoolName}
        onClose={() => {
          hapticsImpactLight();
          setIsAddSchoolModalOpen(false);
        }}
        onNewSchoolNameChange={setNewSchoolName}
        onAddSchool={() => {
          hapticsImpactLight();
          handleAddSchool();
          setIsAddSchoolModalOpen(false);
          setNewSchoolName('');
        }}
      />
    </div>
  );
};

export default SchoolSelector;
